import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import omit from 'lodash.omit';

import { ROUTE } from 'src/constants/routes';
import { MappingField } from 'src/containers/ProjectDiagram/types/data';
import {
  connectionAsyncActions,
  connectionsSlice,
} from 'src/store/connections/connections.slice';
import {
  EntityDrawer,
  EntityDrawerType,
  ProjectSortOption,
  UiState,
  createInitialUiState,
} from 'src/store/ui/types';

export const uiSlice = createSlice({
  name: 'ui',
  initialState: createInitialUiState,
  reducers: {
    updatedMappingFilters: (
      state,
      action: PayloadAction<{
        projectId: string;
        mappingId: string;
        filtersByIri: Record<string, boolean>;
      }>
    ) => {
      const { projectId, mappingId, filtersByIri } = action.payload;
      if (!state.filtersByProjectMapping[projectId]) {
        state.filtersByProjectMapping[projectId] = {};
      }
      state.filtersByProjectMapping[projectId][mappingId] = {
        ...state.filtersByProjectMapping[projectId][mappingId],
        ...filtersByIri,
      };
    },
    updatedMappingFiltersIri: (
      state,
      action: PayloadAction<{
        projectId: string;
        prevIri: string;
        nextIri: string;
      }>
    ) => {
      const { projectId, prevIri, nextIri } = action.payload;
      Object.entries(state.filtersByProjectMapping[projectId] || {}).forEach(
        ([mappingId, filtersByIri]) => {
          const prevValue = filtersByIri[prevIri];
          if (prevValue === undefined) {
            return;
          }
          filtersByIri[nextIri] = prevValue;
          state.filtersByProjectMapping[projectId][mappingId] = omit(
            filtersByIri,
            prevIri
          );
        }
      );
    },
    changedEntityDrawer: (
      state,
      action: PayloadAction<{ drawer: EntityDrawer; projectId: string }>
    ) => {
      const prevDrawer = state.entityDrawer;
      const { drawer, projectId } = action.payload;

      if (drawer.entityValue === prevDrawer.entityValue) {
        return;
      }

      let history: EntityDrawer[];

      if (
        drawer.type === EntityDrawerType.NONE ||
        projectId !== prevDrawer.projectId
      ) {
        history = [];
      } else {
        history = [
          ...prevDrawer.history,
          {
            entityValue: prevDrawer.entityValue,
            type: prevDrawer.type,
          },
        ];
      }

      state.entityDrawer = {
        ...drawer,
        history,
        projectId,
      };
    },
    changedLocation: (state, _action: PayloadAction<{ route: ROUTE }>) => {
      const initialState = createInitialUiState();
      initialState.isVoiceboxDialogOpen = state.isVoiceboxDialogPendingOpen;
      initialState.isProjectModelDialogOpen =
        state.isProjectModelDialogPendingOpen;
      initialState.isProjectResourceDialogOpen =
        state.isProjectResourceDialogPendingOpen;
      return initialState;
    },
    changeProjectSortOption: (
      state,
      action: PayloadAction<ProjectSortOption>
    ) => {
      state.projectSortOption = action.payload;
    },
    closedSignInDialog: (state) => {
      state.isSignInDialogOpen = false;
    },
    removedKeyFromEntityDrawerHistory: (
      { entityDrawer },
      action: PayloadAction<{ entityValue: string; projectId: string }>
    ) => {
      const { entityValue, projectId } = action.payload;
      if (projectId !== entityDrawer.projectId) {
        return;
      }

      const newHistory = entityDrawer.history.filter(
        (drawerEntry) => drawerEntry.entityValue !== entityValue
      );

      const [newValue, newType] =
        entityDrawer.entityValue === entityValue
          ? [undefined, EntityDrawerType.NONE]
          : [entityDrawer.entityValue, entityDrawer.type];

      Object.assign(entityDrawer, {
        entityValue: newValue,
        history: newHistory,
        type: newType,
      });
    },
    selectedMappingDialogClass: (
      state,
      action: PayloadAction<{
        toValue?: string;
      }>
    ) => {
      const { toValue } = action.payload;
      state.createMappingDialog = {
        ...state.createMappingDialog,
        toValue: state.createMappingDialog.isOpen ? toValue : undefined,
      };
    },
    selectedPreviousEntityDrawer: (
      state,
      action: PayloadAction<{ projectId: string; entityValue: string }>
    ) => {
      if (action.payload.projectId !== state.entityDrawer.projectId) {
        return;
      }

      const history = [...state.entityDrawer.history];

      let entry: EntityDrawer | null = null;
      while (!entry && history.length) {
        const tempEntry = history.pop() as EntityDrawer;
        if (tempEntry.entityValue === action.payload.entityValue) {
          entry = tempEntry;
        }
      }

      if (!entry) {
        return;
      }

      state.entityDrawer = {
        ...entry,
        history,
        projectId: action.payload.projectId,
      };
    },
    toggledCreateClassDrawer: (
      state,
      { payload }: PayloadAction<{ initialValue?: string; isOpen: boolean }>
    ) => {
      const { initialValue, isOpen } = payload;
      state.createClassDrawer = {
        initialValue: isOpen ? initialValue : undefined,
        isOpen,
      };
    },
    toggledCreateMappingDialog: (
      state,
      action: PayloadAction<{
        initialValue?: string;
        isOpen: boolean;
        fromId?: string;
        fields?: Record<string, MappingField>;
        toValue?: string;
      }>
    ) => {
      const { initialValue, isOpen, fromId, fields, toValue } = action.payload;
      state.createMappingDialog = {
        initialValue: isOpen ? initialValue : undefined,
        isOpen,
        fromId: isOpen ? fromId : undefined,
        fields: isOpen ? fields : undefined,
        toValue: isOpen ? toValue : undefined,
      };
    },
    toggledCreateProjectDialog: (
      state,
      { payload }: PayloadAction<{ isOpen: boolean }>
    ) => {
      state.isCreateProjectDialogOpen = payload.isOpen;
    },
    toggledCreateRelationshipDrawer: (
      state,
      {
        payload,
      }: PayloadAction<{
        isOpen: boolean;
        sourceClassIri?: string;
        targetClassIri?: string;
      }>
    ) => {
      const { isOpen, sourceClassIri, targetClassIri } = payload;
      state.createRelationshipDrawer = {
        isOpen,
        sourceClassIri: isOpen ? sourceClassIri : undefined,
        targetClassIri: isOpen ? targetClassIri : undefined,
      };
    },
    toggledCreateSensitivePropertyGroupDrawer: (
      state,
      action: PayloadAction<{ isOpen: boolean; attributeIri?: string }>
    ) => {
      const { isOpen, attributeIri } = action.payload;
      state.createSensitivePropertyGroupDrawer = {
        isOpen,
        attributeIri: isOpen ? attributeIri : undefined,
      };
    },
    toggledFileUploadPreviewDialog: (
      state,
      action: PayloadAction<{
        isOpen: boolean;
        resourceId?: string;
      }>
    ) => {
      const { isOpen, resourceId } = action.payload;
      state.fileUploadPreviewDialog = {
        isOpen,
        resourceId: isOpen ? resourceId : undefined,
      };
    },
    toggledProjectModelDialog: (
      state,
      {
        payload: { isOpen, isPendingOpen },
      }: PayloadAction<{ isOpen?: boolean; isPendingOpen?: boolean }>
    ) => {
      if (isOpen !== undefined) {
        state.isProjectModelDialogOpen = isOpen;
      }
      if (isPendingOpen !== undefined) {
        state.isProjectModelDialogPendingOpen = isPendingOpen;
      }
    },
    toggledProjectResourceDialog: (
      state,
      {
        payload: { isOpen, isPendingOpen },
      }: PayloadAction<{ isOpen?: boolean; isPendingOpen?: boolean }>
    ) => {
      if (isOpen !== undefined) {
        state.isProjectResourceDialogOpen = isOpen;
      }
      if (isPendingOpen !== undefined) {
        state.isProjectResourceDialogPendingOpen = isPendingOpen;
      }
    },
    toggledProjectSettingsDrawer: (
      state,
      { payload }: PayloadAction<{ isOpen: boolean }>
    ) => {
      state.isProjectSettingsDrawerOpen = payload.isOpen;
    },
    toggledPublishProjectDrawer: (
      state,
      { payload }: PayloadAction<{ isOpen: boolean }>
    ) => {
      state.isPublishProjectDrawerOpen = payload.isOpen;
    },
    toggledResourceSuggestionsDialog: (
      state,
      action: PayloadAction<UiState['resourceSuggestionsDialog']>
    ) => {
      state.resourceSuggestionsDialog = action.payload;
    },
    toggledRuleDetailDrawer: (
      state,
      { payload }: PayloadAction<{ ruleId?: string; isOpen: boolean }>
    ) => {
      const { ruleId, isOpen } = payload;
      state.ruleDetailDrawer = {
        ruleId: isOpen ? ruleId : undefined,
        isOpen,
      };
    },
    toggledRuleOverviewDrawer: (
      state,
      {
        payload,
      }: PayloadAction<{
        isOpen: boolean;
      }>
    ) => {
      state.ruleOverviewDrawer = payload;
    },
    toggledVirtualGraphPreviewDialog: (
      state,
      {
        payload: { isOpen, resourceId },
      }: PayloadAction<{
        isOpen: boolean;
        resourceId?: string;
      }>
    ) => {
      state.virtualGraphPreviewDialog = {
        isOpen,
        resourceId: isOpen ? resourceId : undefined,
      };
    },
    toggledVoiceboxDialog: (
      state,
      {
        payload: { isOpen, isPendingOpen },
      }: PayloadAction<{ isOpen?: boolean; isPendingOpen?: boolean }>
    ) => {
      if (isOpen !== undefined) {
        state.isVoiceboxDialogOpen = isOpen;
      }
      if (isPendingOpen !== undefined) {
        state.isVoiceboxDialogPendingOpen = isPendingOpen;
      }
    },
    updatedEntityDrawerValue: (
      state,
      action: PayloadAction<{
        projectId: string;
        prevEntityValue: string;
        entityValue: string;
      }>
    ) => {
      const { prevEntityValue, projectId, entityValue } = action.payload;
      if (projectId !== state.entityDrawer.projectId) {
        return;
      }

      const history = [...state.entityDrawer.history].map((drawerEntry) => ({
        ...drawerEntry,
        entityValue:
          drawerEntry.entityValue === prevEntityValue
            ? entityValue
            : drawerEntry.entityValue,
      }));

      Object.assign(state.entityDrawer, {
        entityValue,
        history,
      });
    },
  },
  extraReducers: ({ addCase }) => {
    addCase(
      connectionsSlice.actions.signedOut,
      (state, { payload: { showDialog = true } }) => {
        state.isSignInDialogOpen = showDialog;
      }
    );
    addCase(
      connectionAsyncActions.requestValidateConnection.fulfilled,
      (state) => {
        state.isSignInDialogOpen = false;
      }
    );
  },
});
